"use client";

/**
 * @file LeadForm.tsx
 * @description Canonical inline lead-capture form. Single engine for all
 *   site lead-form surfaces (homepage CTAs, calculator, encar/auction-sheet,
 *   noscut card, catalog detail). Used directly inline OR wrapped in
 *   LeadFormTrigger for modal presentation. Validates phone (and optionally
 *   name), formats phone input live, persists to localStorage before fetch
 *   (SALES-PERSIST-1), submits to /api/lead, supports both permanent inline
 *   success and timed auto-close success modes.
 *
 *   Since series home-stage-b (step B03) the same engine renders three more
 *   variants behind the `variant` prop — Б «сначала задача, потом контакт»
 *   (two steps), its Б-без-бюджета sub-variant, and В «мягкий» — and can
 *   resolve the variant itself from the sticky A/B assignment (`abTest`).
 *
 * @rule source vs subject contract:
 *   - `source` is a stable channel label suitable for grep'ing
 *     site-leads.log ("calculator", "noscut-card", "catalog-car-detail").
 *     New callers MUST pass it explicitly.
 *   - `subject` is the human-readable detail line shown to the manager
 *     in Telegram ("Расчёт: Toyota Camry 2020 — ≈ 3 200 000 ₽").
 *   - Legacy fallback `source ?? subject ?? "LeadForm"` exists only for
 *     pre-UNIFY-1 callers. See ADR [2026-05-04] UNIFY-1.
 *
 * @rule data-fm-hide="true" on the form root keeps FloatingMessengers
 *   FAB hidden while the form is in viewport (handled by FAB itself
 *   via IntersectionObserver). DO NOT remove the attribute.
 *
 * @rule VARIANT A IS FROZEN (series home-stage-b, invariant B-I1). Every
 *   addition below is behind a conditional that is false for variant A, so
 *   a surface that passes neither `variant` nor `abTest` renders exactly the
 *   markup it rendered before the series and sends exactly the same payload.
 *   The funnel goals are also gated on enrolment — an ordinary surface keeps
 *   firing `lead_submit` + its source goal and nothing else.
 *   ONE OWNER-APPROVED EXCEPTION (2026-10-14): the optional MessengerPicker
 *   block renders in EVERY variant, А included, right under the phone. It
 *   does not change variant А's payload unless the visitor uses it, and it
 *   never adds the «Вариант формы» line (see hasNewFields).
 *   SECOND OWNER-APPROVED EXCEPTION (2026-10-16): every variant, А included,
 *   adds the optional `utm` / `referrer` keys (src/lib/leadAttribution) to
 *   the POST body when the visit carried them. No markup changes; a visitor
 *   without attribution sends exactly the old payload.
 *
 * @rule THE PHONE IS THE ONLY REQUIRED FIELD in every variant (invariant
 *   B-I2), and nothing is pre-selected. «Далее →» never validates: a visitor
 *   who chose nothing on step 1 is allowed through to the phone — that is a
 *   measured outcome, not an error state.
 *
 * @rule BUDGET IS OPTIONAL AND ONLY IN Б (invariant B-I3). The Б-без-бюджета
 *   sub-variant exists to measure what asking for it costs, so the budget row
 *   must never appear outside `variant === "B"`.
 *
 * @rule SECTION RULES ARE «─── LABEL ───», NOT PADDED TO A COLUMN. There is no
 *   local clone of this repository: every edit sends this file whole through
 *   the GitHub connector, and a dash run padded to column 73 cannot be
 *   retyped byte-for-byte. Two attempts on 2026-09-21 produced two different
 *   counts. Three dashes each side is reproducible, so the file's hash now
 *   changes only when its code does.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, CheckCircle } from "lucide-react";
import { saveBeforeSend, markConfirmed } from "@/lib/leadPersistence";
import { trackGoal } from "@/lib/metrika";
import { goalToken } from "@/lib/goalToken";
import { getVariant } from "@/lib/abVariant";
import {
  LEADFORM_TEST_ID,
  LEADFORM_VARIANTS,
  VARIANT_LABEL,
  isLeadFormVariant,
  type LeadFormVariant,
} from "@/lib/leadFormVariant";
import { composeLeadMessage } from "@/lib/leadMessage";
import { readLeadAttribution } from "@/lib/leadAttribution";
import ChipGroup from "@/components/ui/ChipGroup";
import MessengerPicker, { type MessengerValue } from "@/components/MessengerPicker";

// ─── METRIKA GOAL MAP ───

/**
 * Map from stable kebab-case `source` label (passed by every UNIFY-1+
 * caller) to the snake_case Metrika goal identifier configured in the
 * Metrika UI for counter 106847609.
 *
 * @rule This map is the single source of truth for source → goal name.
 *       Adding a new lead surface = add a JSX caller with a new source
 *       label here AND create the goal in Metrika UI. Both must happen
 *       in the same commit / same operator session — a source label
 *       without a goal entry produces only `lead_submit` (no surface
 *       attribution); a goal entry without a Metrika UI definition
 *       silently drops to 0 in reports (Metrika ignores unknown goal
 *       names with no error).
 *
 * @rule Legacy callers (pre-UNIFY-1) and any future surface that
 *       forgets to pass a `source` still get counted in `lead_submit`
 *       (the global counter fired unconditionally below). Only the
 *       surface-specific dimension is lost — acceptable for a stale
 *       caller, not a regression.
 */
const LEAD_GOAL_BY_SOURCE: Record<string, string> = {
  // "main-cta" is kept for history (leads/goals before 2026-09-21); Hero and ContactCTA now
  // report separately as "main-hero" / "main-bottom" (series home-stage-a, step A07).
  "main-cta": "lead_main_cta",
  "main-hero": "lead_main_hero",
  "main-bottom": "lead_main_bottom",
  "tools-calculator": "lead_calculator",
  "tools-landing": "lead_tools_landing",
  "news-landing": "lead_news_landing",
  "catalog-car-card": "lead_catalog_car",
  "catalog-car-detail": "lead_catalog_car_detail",
  "catalog-car-wholesale": "lead_catalog_car_wholesale",
  "noscut-card": "lead_noscut_card",
  "noscut-detail": "lead_noscut_detail",
  "noscut-wholesale": "lead_noscut_wholesale",
  "noscut-no-model": "lead_noscut_no_model",
  "tools-auction-sheet": "lead_tools_auction_sheet",
  "tools-customs": "lead_tools_customs",
  "tools-encar": "lead_tools_encar",
  "tools-calculator-result": "lead_tools_calculator_result",
  "about-tail": "lead_about_tail",
  "tools-landing-tail": "lead_tools_landing_tail",
  "tools-calculator-tail": "lead_tools_calculator_tail",
  "tools-customs-tail": "lead_tools_customs_tail",
  "tools-auction-sheet-tail": "lead_tools_auction_sheet_tail",
  "tools-encar-tail": "lead_tools_encar_tail",
  "blog-tail": "lead_blog_tail",
  "news-tail": "lead_news_tail",
  "catalog-tail": "lead_catalog_tail",
  "catalog-car-detail-tail": "lead_catalog_car_detail_tail",
  "catalog-noscut-tail": "lead_catalog_noscut_tail",
  "reviews-tail": "lead_reviews_tail",
  "promo-eva-mats": "lead_promo_eva",
  // Series home-stage-c: the inspection page (C04) and the strip under the
  // catalog examples (C05). Both entries land in one commit on purpose — this
  // file is expensive to move through the connector, and a map entry with no
  // caller yet fires nothing. The matching goals still have to be created in
  // the Metrika UI; until then they count only in the site's own journal.
  "how-we-check-tail": "lead_how_we_check_tail",
  "catalog-home-not-found": "lead_catalog_home_not_found",
  // Series inspection-cases, step S3: the «Нашли машину сами? — Проверить
  // машину» strip in the homepage inspection block and on /how-we-check.
  "home-vin-check": "lead_home_vin_check",
  "how-we-check-vin-check": "lead_how_we_check_vin_check",
};

// ─── A/B VARIANTS ───

/**
 * @rule THE VARIANT CONSTANTS LIVE IN @/lib/leadFormVariant, NOT HERE (series
 *       home-stage-b, step B04c). This file is a "use client" module, so every
 *       VALUE it exports becomes a client reference on the server — a stub
 *       that throws when server code touches it. The homepage reads the
 *       assignment cookie on the server and needs the real id and the real
 *       list, so it takes them from the pure module. The type re-export below
 *       is safe (types are erased) and is kept so existing callers that import
 *       `type LeadFormVariant` from this file keep working.
 */
export type { LeadFormVariant } from "@/lib/leadFormVariant";

// Approved texts Т-23…Т-28 from the owner's ТЗ, verbatim.
const TEXTS = {
  softHeading: "Подберём автомобиль под ваш запрос и бюджет",
  softWishesPlaceholder: "Какую машину ищете? (необязательно)",
  submit: "Подобрать варианты",
  stepHeading: "Расскажите, что ищете — подберём варианты под ваш запрос и бюджет",
  stepOneCaption: "Шаг 1 из 2 · всё необязательно",
  stepTwoCaption: "Шаг 2 из 2",
  wishesPlaceholder: "Что важно: модель, полный привод, год…",
  budgetLabel: "Примерный бюджет — поможет подобрать точнее (можно пропустить)",
  next: "Далее →",
  back: "← Назад",
  promise: "Бесплатно · На связи почти 24/7 — ответим, как только увидим",
} as const;

const BODY_OPTIONS = ["Кроссовер", "Седан", "Минивэн", "7 мест", "Другое"] as const;
const ORIGIN_OPTIONS = ["Не важно — где выгоднее", "Китай", "Корея", "Япония"] as const;
const BUDGET_OPTIONS = ["до 1,5 млн", "1,5–2,5", "2,5–4", "4+"] as const;

/**
 * Client-side cap on the new free-text field. The visitor's own comment in
 * variant А is deliberately NOT capped by an attribute — see the rule at the
 * textarea. The composed message is capped as a whole in composeLeadMessage.
 */
const WISHES_MAX = 120;

// ─── HELPERS ───

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  let result = "+7";
  if (digits.length > 1) result += " (" + digits.slice(1, 4);
  if (digits.length >= 4) result += ") " + digits.slice(4, 7);
  if (digits.length >= 7) result += "-" + digits.slice(7, 9);
  if (digits.length >= 9) result += "-" + digits.slice(9, 11);
  return result;
}

// ─── TYPES ───

const leadSchemaPhoneOnly = z.object({
  phone: z.string().refine(
    (val) => val.replace(/\D/g, "").length >= 11,
    "Введите телефон",
  ),
  name: z.string().optional(),
  comment: z.string().optional(),
});

// Currently unused — see ADR [2026-05-07] LEADFORM-REDESIGN. Kept for potential future re-introduction.
const leadSchemaWithName = z.object({
  phone: z.string().refine(
    (val) => val.replace(/\D/g, "").length >= 11,
    "Введите телефон",
  ),
  name: z.string().min(1, "Введите имя"),
  comment: z.string().optional(),
});

// Wider variant — name typed as string. RHF permissive-type pattern:
// runtime validation is selected per-render via useMemo on `requireName`,
// but the static type covers both schemas.
type LeadFormData = z.infer<typeof leadSchemaWithName>;

interface LeadFormProps {
  /** Free-text human-readable subject shown to the manager in Telegram */
  subject?: string;
  /** Submit button label */
  ctaLabel?: string;
  /** Tighter padding for nested cards / sidebar slots */
  compact?: boolean;
  /**
   * Stable channel label for site-leads.log analytics ("calculator",
   * "noscut-card", "catalog-car-detail"). Falls back to `subject` then
   * "LeadForm" if absent. See ADR [2026-05-04] UNIFY-1.
   */
  source?: string;
  /**
   * @deprecated Since 2026-05-07. The form always uses
   * phone-only schema. Passing true triggers a console warning.
   * Field will be removed in a future cleanup.
   */
  requireName?: boolean;
  /**
   * "inline" (default) — success state stays visible permanently.
   * "auto-close" — success UI shows for `autoCloseMs` then `onSuccess`
   * fires. Used by LeadFormTrigger to close the modal after submit.
   */
  successMode?: "inline" | "auto-close";
  /** Delay before onSuccess fires when successMode="auto-close". Default 3000ms. */
  autoCloseMs?: number;
  /** Called after successful submit + autoCloseMs delay (auto-close mode only). */
  onSuccess?: () => void;
  /**
   * Fallback comment used when the user submits with an empty comment field.
   * Appears in the manager's Telegram notification and in site-leads.log as
   * the lead's `message`. Useful when a tool surface knows the context but
   * the user did not type anything (e.g. "Расчёт стоимости с калькулятора").
   *
   * Recommendation: keep it short (≤200 chars). Long values will not fit
   * cleanly in Telegram cards.
   *
   * If both `data.comment` (user input) and `fallbackComment` are empty,
   * `message` is sent as `undefined` (current behavior preserved).
   */
  fallbackComment?: string;
  /**
   * Placeholder of variant А's free-text field. The default is the historic
   * «Имя, комментарий (необязательно)», so every existing surface renders
   * byte-identical markup (B-I1). A surface that expects something specific
   * in that field says so here — the VIN-check strip asks for a link or a
   * VIN. Series inspection-cases, step S3.
   */
  commentPlaceholder?: string;
  /**
   * Force a variant. Default "A" — the behaviour every pre-B03 surface has.
   * An explicit variant also enrols the surface in the funnel goals, which is
   * what makes `?ab_leadform=B` on a live page a complete QA path.
   */
  variant?: LeadFormVariant;
  /**
   * Resolve the variant from the sticky A/B assignment after mount. Ignored
   * when `variant` is given. The server still renders variant A.
   */
  abTest?: boolean;
  /**
   * Funnel-goal label for this slot. Defaults to `source`.
   *
   * @rule THIS IS THE REPORTING BUCKET, NOT THE SURFACE. `source` stays
   *       unique per surface and keeps feeding the existing
   *       `lead_<source>` goals, so per-surface lead attribution is never
   *       affected by anything done here. `place` exists so a surface can
   *       report its funnel under a label of its own choosing — or share
   *       one with sibling surfaces when that is what the question needs.
   *
   * @rule ONE TEST PER SURFACE OR PAGE TYPE, NOT ONE TEST EVERYWHERE
   *       (owner decision 2026-09-21, step B06). The lead-form A/B test
   *       runs on the homepage only. A capture block is designed for the
   *       page it sits on: the auction-sheet result form asks about the
   *       sheet, the calculator's asks about the calculation. Spreading
   *       one averaged variant across every surface measures nothing
   *       anybody can act on, and splits the traffic past readability.
   *       A surface that wants its own experiment passes its own `place`
   *       and its own `variant` — the engine is shared, the question is
   *       not.
   */
  place?: string;
}

// ─── COMPONENT ───

export default function LeadForm({
  subject,
  ctaLabel = "Оставить заявку",
  compact = false,
  source,
  requireName = false,
  successMode = "inline",
  autoCloseMs = 3000,
  onSuccess,
  fallbackComment,
  commentPlaceholder = "Имя, комментарий (необязательно)",
  variant,
  abTest = false,
  place,
}: LeadFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ─── A/B state ───
  // The surface takes part in the test when it forces a variant or asks for
  // the sticky assignment. Everything below is inert for every other caller.
  const enrolled = variant !== undefined || abTest;
  const [resolvedVariant, setResolvedVariant] = useState<LeadFormVariant>(variant ?? "A");
  // Until the assignment is read, the funnel must not report a variant the
  // visitor may never see. False only while an assignment is pending.
  const [variantSettled, setVariantSettled] = useState<boolean>(variant !== undefined || !abTest);

  useEffect(() => {
    if (variant !== undefined || !abTest) return;
    const assigned = getVariant(LEADFORM_TEST_ID, LEADFORM_VARIANTS);
    if (isLeadFormVariant(assigned)) setResolvedVariant(assigned);
    setVariantSettled(true);
  }, [variant, abTest]);

  const isB = resolvedVariant === "B" || resolvedVariant === "B-nobudget";
  const isC = resolvedVariant === "C";
  const showBudget = resolvedVariant === "B";

  const [step, setStep] = useState<1 | 2>(1);
  const [bodyChoice, setBodyChoice] = useState<string | null>(null);
  const [originChoice, setOriginChoice] = useState<string | null>(null);
  const [budgetChoice, setBudgetChoice] = useState<string | null>(null);
  const [messenger, setMessenger] = useState<MessengerValue>({ messenger: null, login: "" });
  const [wishes, setWishes] = useState("");

  // ─── Funnel goals ───
  const placeLabel = place ?? source ?? "unknown";
  // @rule NORMALISED, NOT INTERPOLATED RAW (series home-stage-b, step B04d).
  //       Place labels are kebab-case and variant names are capitalised, so
  //       the plain template produced ids like `form_view_main-hero_B` —
  //       outside the [a-z0-9_] alphabet both Metrika and /api/track require.
  //       The collector answers 204 to everything, so those events were
  //       counted nowhere and nothing reported the loss. Keep goalToken here.
  const goalSuffix = `${goalToken(placeLabel)}_${goalToken(resolvedVariant)}`;
  const formRef = useRef<HTMLFormElement | null>(null);
  const viewFiredRef = useRef(false);
  const startFiredRef = useRef(false);

  // form_view — once per page view, when the form actually enters the
  // viewport. A form the visitor scrolled past is not a form they were shown.
  useEffect(() => {
    if (!enrolled || !variantSettled || viewFiredRef.current) return;
    const element = formRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !viewFiredRef.current) {
            viewFiredRef.current = true;
            trackGoal(`form_view_${goalSuffix}`);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enrolled, variantSettled, goalSuffix]);

  // form_start — the first time the visitor touches anything in the form.
  const handleFirstInteraction = useCallback(() => {
    if (!enrolled || startFiredRef.current) return;
    startFiredRef.current = true;
    trackGoal(`form_start_${goalSuffix}`);
  }, [enrolled, goalSuffix]);

  const schema = useMemo(
    () => leadSchemaPhoneOnly,
    [],
  );

  useEffect(() => {
    if (requireName === true) {
      console.warn(
        "[LeadForm] requireName prop is deprecated and ignored. " +
        "The form now always uses phone-only schema. " +
        "Remove requireName from the call site."
      );
    }
  }, [requireName]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: zodResolver(schema),
    defaultValues: { phone: "" },
  });

  const inputCls = compact
    ? "w-full bg-white rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
    : "mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-text outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary md:text-sm";

  const buttonCls = `cursor-pointer flex w-full items-center justify-center gap-2 rounded-xl bg-primary font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-70 ${compact ? "px-4 py-2.5 text-sm" : "px-6 py-3 text-base"}`;

  // Б and В submit with the approved label; А keeps whatever the caller passed.
  const effectiveCta = isB || isC ? TEXTS.submit : ctaLabel;

  const onSubmit = async (data: LeadFormData) => {
    setStatus("loading");
    setErrorMessage(null);
    // The messenger is deliberately NOT a "new field" here: it renders on
    // every surface, and a tail form must not start printing «Вариант формы».
    const hasNewFields = Boolean(
      bodyChoice || originChoice || budgetChoice || wishes.trim(),
    );
    // @rule The variant line travels only when it carries information: a
    //       non-A variant, or an А surface where the visitor filled one of
    //       the new fields. Plain А keeps its byte-identical payload.
    const message = composeLeadMessage({
      body: bodyChoice,
      origin: originChoice,
      budget: showBudget ? budgetChoice : null,
      wishes,
      messenger: messenger.messenger,
      messengerLogin: messenger.login,
      comment: data.comment,
      fallbackComment,
      variantLine:
        resolvedVariant !== "A" || hasNewFields
          ? `Вариант формы: ${VARIANT_LABEL[resolvedVariant]}`
          : undefined,
    });
    const persistedId = saveBeforeSend({
      phone: data.phone,
      name: data.name || undefined,
      message,
      source: source ?? subject ?? "LeadForm",
      subject: subject || undefined,
    });
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: data.phone,
          name: data.name || undefined,
          message,
          source: source ?? subject ?? "LeadForm",
          subject: subject || undefined,
          // @rule Read at SUBMIT time, never at render time. The App Router
          //       does not remount this form on client-side navigation when
          //       it lives inside a persistent layout, so a value captured at
          //       render would report the page the visitor LANDED on rather
          //       than the one they submitted from — stale for a large share
          //       of real submits.
          // @rule `pathname` only — never `href`. The query string may carry
          //       personal data and must not reach the managers' Telegram
          //       group or site-leads.log. Sending the full URL and letting
          //       the server strip it is NOT acceptable: by then it has
          //       already crossed the network. The server sanitizes again
          //       (ADR N-LEAD-PAGEPATH-1) — neither side relies on the other.
          // @rule Guarded, not defaulted. Without a browser the key is OMITTED
          //       entirely, and /api/lead treats absence as "unknown" — the
          //       same shape every cached bundle keeps sending for days.
          ...(typeof window !== "undefined"
            ? { pagePath: window.location.pathname }
            : {}),
          // Traffic attribution (UTM + referrer host) of the visit that
          // brought the visitor; {} when unknown — see src/lib/leadAttribution.
          ...readLeadAttribution(),
        }),
      });
      if (!res.ok) {
        try {
          const body = await res.json();
          setErrorMessage(body.error || "Ошибка отправки. Попробуйте позже.");
        } catch {
          setErrorMessage("Ошибка отправки. Попробуйте позже.");
        }
        setStatus("error");
        return;
      }
      markConfirmed(persistedId);
      // @rule Fire goals AFTER markConfirmed (success persisted) but
      //       BEFORE setStatus("success") (UI flip). Order does not
      //       affect correctness — trackGoal is fire-and-forget — but
      //       grouping all post-success side-effects together makes
      //       diffs and code review easier.
      trackGoal("lead_submit");
      const specificGoal = LEAD_GOAL_BY_SOURCE[source ?? ""];
      if (specificGoal) trackGoal(specificGoal);
      // Funnel goal — only for surfaces taking part in the test, so the
      // existing 30 surfaces keep firing exactly two goals as before.
      if (enrolled) trackGoal(`lead_${goalSuffix}`);
      setStatus("success");
    } catch {
      setErrorMessage("Ошибка отправки. Попробуйте позже.");
      setStatus("error");
    }
  };

  // Auto-close: when in auto-close mode and submit succeeded, fire
  // onSuccess after autoCloseMs. Cleans up if status changes or unmounts.
  useEffect(() => {
    if (status !== "success" || successMode !== "auto-close") return;
    const t = setTimeout(() => {
      onSuccess?.();
    }, autoCloseMs);
    return () => clearTimeout(t);
  }, [status, successMode, autoCloseMs, onSuccess]);

  if (status === "success") {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <CheckCircle className="h-12 w-12 text-green-500" />
        <p className="text-lg font-medium text-gray-900">Заявка принята!</p>
        <p className="text-sm text-gray-500">На связи почти 24/7 — напишите в любое время, ответим, как только увидим; в рабочие часы по Владивостоку — в течение часа</p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)} data-fm-hide="true" className={`space-y-3 ${compact ? "text-sm" : ""}`}>
      {isC && (
        <>
          <p className="text-base font-semibold text-text">{TEXTS.softHeading}</p>
          <div>
            <input
              type="text"
              value={wishes}
              maxLength={WISHES_MAX}
              onFocus={handleFirstInteraction}
              onChange={(e) => {
                handleFirstInteraction();
                setWishes(e.target.value);
              }}
              placeholder={TEXTS.softWishesPlaceholder}
              className={inputCls}
            />
          </div>
        </>
      )}

      {isB && step === 1 && (
        <>
          <p className="text-base font-semibold text-text">{TEXTS.stepHeading}</p>
          <p className="text-xs text-text-muted">{TEXTS.stepOneCaption}</p>

          <ChipGroup
            label="Кузов"
            name="lead-body"
            options={BODY_OPTIONS}
            value={bodyChoice}
            onChange={(next) => {
              handleFirstInteraction();
              setBodyChoice(next);
            }}
          />
          <ChipGroup
            label="Откуда"
            name="lead-origin"
            options={ORIGIN_OPTIONS}
            value={originChoice}
            onChange={(next) => {
              handleFirstInteraction();
              setOriginChoice(next);
            }}
          />

          <div>
            <input
              type="text"
              value={wishes}
              maxLength={WISHES_MAX}
              onFocus={handleFirstInteraction}
              onChange={(e) => {
                handleFirstInteraction();
                setWishes(e.target.value);
              }}
              placeholder={TEXTS.wishesPlaceholder}
              className={inputCls}
            />
          </div>

          {showBudget && (
            <ChipGroup
              label={TEXTS.budgetLabel}
              name="lead-budget"
              options={BUDGET_OPTIONS}
              value={budgetChoice}
              onChange={(next) => {
                handleFirstInteraction();
                setBudgetChoice(next);
              }}
            />
          )}

          <button
            type="button"
            onClick={() => {
              handleFirstInteraction();
              setStep(2);
            }}
            className={buttonCls}
          >
            {TEXTS.next}
          </button>
        </>
      )}

      {isB && step === 2 && <p className="text-xs text-text-muted">{TEXTS.stepTwoCaption}</p>}

      {(!isB || step === 2) && (
        <>
          <div>
            <input
              {...register("phone")}
              type="tel"
              placeholder={isB ? "Телефон *" : "Телефон"}
              className={inputCls}
              onFocus={handleFirstInteraction}
              onChange={(e) => {
                handleFirstInteraction();
                const formatted = formatPhone(e.target.value);
                e.target.value = formatted;
                register("phone").onChange(e);
              }}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
            )}
          </div>

          <MessengerPicker
            value={messenger}
            onChange={setMessenger}
            onInteract={handleFirstInteraction}
            inputClassName={inputCls}
          />

          {!isB && !isC && (
            <div>
              {/* @rule NO maxLength here. It would add a `maxlength` attribute to
                  variant А's rendered HTML and break the byte-identical snapshot
                  the series promises (acceptance point 1). The long-comment case is
                  handled where it actually matters — composeLeadMessage keeps the
                  whole message under the server's 1000-character ceiling. */}
              <textarea
                {...register("comment")}
                rows={2}
                placeholder={commentPlaceholder}
                className={`resize-none ${inputCls}`}
                onFocus={handleFirstInteraction}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className={buttonCls}
          >
            {status === "loading" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Отправка…
              </>
            ) : (
              effectiveCta
            )}
          </button>

          {isB && (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="cursor-pointer w-full text-center text-sm text-text-muted underline-offset-2 hover:underline"
            >
              {TEXTS.back}
            </button>
          )}

          {status === "error" && errorMessage && (
            <p className="text-center text-sm text-red-500">{errorMessage}</p>
          )}

          {(isB || isC) && (
            <p className="mt-3 text-center text-xs text-text-muted">{TEXTS.promise}</p>
          )}

          <p className="mt-3 text-center text-xs text-gray-500 leading-relaxed">
            Нажимая «{effectiveCta}», вы соглашаетесь с обработкой персональных данных в соответствии с{" "}
            <Link href="/privacy" className="text-primary underline">политикой конфиденциальности</Link>
          </p>
        </>
      )}
    </form>
  );
}
